"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/components/toast-provider";
import * as s from "@/lib/styles";

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const { showToast } = useToast();
  const router = useRouter();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    if (user.mustChangePassword) {
      router.replace("/change-password");
    } else {
      router.replace("/photos");
    }
  }, [loading, user, router]);

  if (loading || user) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
        <p className="text-muted">Loading...</p>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(username, password);
      if (u.mustChangePassword) {
        router.push("/change-password");
      } else {
        router.push("/photos");
      }
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6">
        <h1 className="text-2xl font-bold text-cyan text-center">Sign In</h1>
        <div className={s.card}>
          <div className={s.fieldRow}>
            <label className={s.label}>Username or email</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={s.input}
              autoFocus
              autoComplete="username"
            />
          </div>
          <div className={s.fieldRow}>
            <label className={s.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={s.input}
              autoComplete="current-password"
            />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <Link href="/forgot-password" className="text-sm text-muted hover:text-accent">
            Forgot password?
          </Link>
          <button type="submit" disabled={submitting} className={s.btnPrimary}>
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
}
